import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet, subscribeToDataInvalidation } from '../utils/api';
import DataTable from '../components/DataTable';
import ExportButton from '../components/ExportButton';
import CreateCropModal from '../components/crops/CreateCropModal';
import CropDetailModal from '../components/crops/CropDetailModal';
import MapBaseComponent from '../components/maps/MapBaseComponent';
import { RWANDA_DEFAULTS } from '../utils/geospatial';
import { useCurrency } from '../hooks/useCurrency';
import { cropKey, normalizeCropName } from '../utils/cropCatalog';
import {
  CurrencyDollarIcon,
  CubeIcon,
  ChartPieIcon,
  ChartBarIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  ArrowPathIcon,
  SparklesIcon,
  ScaleIcon,
  CalendarDaysIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

interface CropData {
  crop_type: string;
  cohort_name: string;
  cropping_system: string;
  planted_area_ha: number;
  harvest_date: string;
  total_production_kg: number;
  gross_revenue: number;
  total_expenses: number;
  profit_margin: number;
  farmer_count: number;
  season?: string;
  cycle_count?: number;
}

interface CropsSummary {
  totalCropTypes: number;
  totalProduction: number;
  grossRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
}

interface Analytics {
  productionTrends: Array<{
    month: string;
    crop_type: string;
    production_kg: number;
  }>;
  qualityDistribution: Array<any>;
  revenueByType: Array<{
    crop_type: string;
    revenue: number;
    net_revenue: number;
  }>;
}

const parseMapBoundary = (value: any) => {
  if (!value) return [];
  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((point: any) => Array.isArray(point)
      ? { lat: Number(point[0]), lng: Number(point[1]) }
      : { lat: Number(point.lat ?? point.latitude), lng: Number(point.lng ?? point.longitude) })
    .filter((point: any) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
};

const CropsPage: React.FC = () => {
  const { format: formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CropsSummary | null>(null);
  const [crops, setCrops] = useState<CropData[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCropType, setSelectedCropType] = useState<string>('');
  const [mapFarmers, setMapFarmers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const currentPage = pagination.page;
      const currentLimit = pagination.limit;
      const [summaryData, cropsData, analyticsData, farmersGeo] = await Promise.all([
        apiGet<CropsSummary>('/api/crops/summary'),
        apiGet<any>(`/api/crops?page=${currentPage}&limit=${currentLimit}`),
        apiGet<Analytics>('/api/crops/analytics'),
        apiGet<any[]>('/api/maps/farm-plots').catch(() => []),
      ]);

      setSummary(summaryData);
      
      const cropsArray = cropsData.crops || [];
      setCrops(cropsArray);
      
      setPagination(cropsData.pagination || {
        page: currentPage,
        limit: currentLimit,
        total: cropsArray.length,
        totalPages: 1
      });
      setAnalytics(analyticsData);
      setMapFarmers((farmersGeo || []).map((farmer: any) => ({
        id: Number(farmer.id),
        name: farmer.name || farmer.full_name || farmer.plot_code || `Field ${farmer.id}`,
        household_type: 'farmer',
        plot_boundary_coordinates: parseMapBoundary(farmer.boundary_coordinates || farmer.plot_boundary_coordinates),
        cohort_name: farmer.crop_type || '',
        crop_type: farmer.crop_type,
        cropping_system: farmer.crop_type,
        cohort_id: 0,
        plot_area_hectares: Number(farmer.area_hectares || farmer.plot_area_hectares || 0),
      })).filter((farmer: any) => farmer.plot_boundary_coordinates.length >= 3));
    } catch (error) {
      console.error('Failed to fetch crops data:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Subscribe to data invalidation events
  useEffect(() => {
    const unsubscribe = subscribeToDataInvalidation(() => {
      fetchData();
    });
    return unsubscribe;
  }, [fetchData]);

  // Fetch crop details
  const fetchCropDetails = async (cropType: string) => {
    try {
      const details = await apiGet(`/api/crops/${encodeURIComponent(cropType)}`);
      setSelectedCrop(details);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to fetch crop details:', error);
      alert('Failed to load crop details');
    }
  };

  const getSeason = (date?: string) => {
    if (!date) return 'Unscheduled';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return 'Unscheduled';
    const month = parsed.getMonth() + 1;
    const year = parsed.getFullYear();
    const label = month <= 2
      ? 'Season A harvest'
      : month <= 5
        ? 'Season B harvest'
        : month <= 8
          ? 'Season C harvest'
          : 'Late season harvest';
    return `${label} ${year}`;
  };

  const cropPortfolio = useMemo(() => {
    const grouped = new Map<string, CropData>();

    crops.forEach((crop) => {
      const key = cropKey(crop.crop_type || 'Unknown crop');
      const current = grouped.get(key);
      const production = parseFloat(crop.total_production_kg as any) || 0;
      const revenue = parseFloat(crop.gross_revenue as any) || 0;
      const expenses = parseFloat(crop.total_expenses as any) || 0;
      const area = parseFloat(crop.planted_area_ha as any) || 0;
      const farmers = parseInt(crop.farmer_count as any) || 0;

      if (!current) {
        grouped.set(key, {
          ...crop,
          crop_type: normalizeCropName(crop.crop_type) || 'Unknown crop',
          planted_area_ha: area,
          total_production_kg: production,
          gross_revenue: revenue,
          total_expenses: expenses,
          farmer_count: farmers,
          profit_margin: revenue > 0 ? ((revenue - expenses) / revenue) * 100 : 0,
          season: getSeason(crop.harvest_date),
          cycle_count: 1,
        });
        return;
      }

      const latestDate = !current.harvest_date || (crop.harvest_date && new Date(crop.harvest_date) > new Date(current.harvest_date))
        ? crop.harvest_date
        : current.harvest_date;
      const nextRevenue = (parseFloat(current.gross_revenue as any) || 0) + revenue;
      const nextExpenses = (parseFloat(current.total_expenses as any) || 0) + expenses;

      grouped.set(key, {
        ...current,
        harvest_date: latestDate,
        planted_area_ha: (parseFloat(current.planted_area_ha as any) || 0) + area,
        total_production_kg: (parseFloat(current.total_production_kg as any) || 0) + production,
        gross_revenue: nextRevenue,
        total_expenses: nextExpenses,
        farmer_count: Math.max(parseInt(current.farmer_count as any) || 0, farmers),
        profit_margin: nextRevenue > 0 ? ((nextRevenue - nextExpenses) / nextRevenue) * 100 : 0,
        season: getSeason(latestDate),
        cycle_count: (current.cycle_count || 1) + 1,
      });
    });

    return Array.from(grouped.values()).sort((a, b) => a.crop_type.localeCompare(b.crop_type));
  }, [crops]);

  const selectedCropCycles = useMemo(() => {
    const cropType = selectedCropType || cropPortfolio[0]?.crop_type || '';
    return crops
      .filter((crop) => cropKey(crop.crop_type) === cropKey(cropType))
      .map((crop) => ({ ...crop, season: getSeason(crop.harvest_date) }));
  }, [cropPortfolio, crops, selectedCropType]);

  const seasonalGroups = useMemo(() => {
    return selectedCropCycles.reduce<Record<string, CropData[]>>((acc, crop) => {
      const season = crop.season || 'Unscheduled';
      acc[season] = acc[season] || [];
      acc[season].push(crop);
      return acc;
    }, {});
  }, [selectedCropCycles]);

  const cropMapFarmers = useMemo(() => {
    const selectedKey = cropKey(selectedCropType || cropPortfolio[0]?.crop_type || '');
    if (!selectedKey) return mapFarmers;
    return mapFarmers.filter((field: any) => cropKey(field.crop_type || field.cropping_system || field.cohort_name) === selectedKey);
  }, [cropPortfolio, mapFarmers, selectedCropType]);

  // Format currency - now using the hook
  // const formatCurrency = (amount: number) => {
  //   return new Intl.NumberFormat('en-RW', {
  //     style: 'currency',
  //     currency: 'RWF',
  //     minimumFractionDigits: 0
  //   }).format(amount);
  // };

  // Format number
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Table columns
  const columns = [
    {
      key: 'crop_type',
      label: 'Crop Type',
      render: (value: string) => (
        <button
          onClick={() => setSelectedCropType(value)}
          className="font-semibold text-emerald-700 hover:text-emerald-900"
        >
          {value}
        </button>
      )
    },
    {
      key: 'season',
      label: 'Current Window',
      render: (value: string, row: CropData) => (
        <div>
          <div className="font-semibold text-gray-900">{value || getSeason(row.harvest_date)}</div>
          <div className="text-xs text-gray-500">{row.cycle_count || 1} cycle{(row.cycle_count || 1) === 1 ? '' : 's'}</div>
        </div>
      )
    },
    {
      key: 'planted_area_ha',
      label: 'Planted Area (ha)',
      render: (value: number) => {
        const num = parseFloat(value as any) || 0;
        return num.toFixed(2);
      }
    },
    {
      key: 'harvest_date',
      label: 'Harvest Date',
      render: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
    {
      key: 'total_production_kg',
      label: 'Production (kg)',
      render: (value: number) => formatNumber(parseFloat(value as any) || 0)
    },
    {
      key: 'gross_revenue',
      label: 'Revenue',
      render: (value: number) => formatCurrency(parseFloat(value as any) || 0)
    },
    {
      key: 'total_expenses',
      label: 'Expenses',
      render: (value: number) => formatCurrency(parseFloat(value as any) || 0)
    },
    {
      key: 'profit_margin',
      label: 'Profit Margin (%)',
      render: (value: number) => {
        const num = parseFloat(value as any) || 0;
        return (
          <span className={`font-semibold ${num >= 30 ? 'text-green-600' : num >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
            {num.toFixed(2)}%
          </span>
        );
      }
    },
    {
      key: 'farmer_count',
      label: 'Farmers',
      render: (value: number) => parseInt(value as any) || 0
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, row: CropData) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchCropDetails(row.crop_type);
            }}
            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors"
            title="View Details"
          >
            <EyeIcon className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="relative">
          <ArrowPathIcon className="animate-spin text-blue-600 w-16 h-16" />
          <SparklesIcon className="absolute top-0 right-0 text-green-500 w-6 h-6 animate-pulse" />
        </div>
        <p className="mt-4 text-gray-600 font-medium">Loading Crops Intelligence...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50 p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        
        {/* Compact Header */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Crops Intelligence</h1>
                <p className="text-xs text-gray-500">{summary?.totalCropTypes || 0} crop types • {formatNumber(summary?.totalProduction || 0)} kg harvested</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              >
                <FunnelIcon className="w-4 h-4" />
                Filters
              </button>
              <ExportButton
                onExport={() => {
                  const csv = cropPortfolio.map(c => Object.values(c).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'crops-report.csv';
                  a.click();
                }}
                label="Export"
                variant="secondary"
              />
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                New Crop
              </button>
            </div>
          </div>
        </div>

        {/* Compact KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <ScaleIcon className="w-5 h-5 text-blue-500" />
              <span className="text-xs font-semibold text-green-600">↑ 12%</span>
            </div>
            <p className="text-xs text-gray-600 mb-1">Production</p>
            <p className="text-xl font-bold text-gray-900">{formatNumber(summary?.totalProduction || 0)}</p>
            <p className="text-xs text-gray-500">kg</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <CurrencyDollarIcon className="w-5 h-5 text-green-500" />
              <span className="text-xs font-semibold text-green-600">↑ 8%</span>
            </div>
            <p className="text-xs text-gray-600 mb-1">Revenue</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary?.grossRevenue || 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <ChartPieIcon className="w-5 h-5 text-emerald-500" />
              <span className="text-xs font-semibold text-green-600">↑ 5%</span>
            </div>
            <p className="text-xs text-gray-600 mb-1">Profit Margin</p>
            <p className="text-xl font-bold text-gray-900">{summary?.profitMargin?.toFixed(1) || 0}%</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-2">
              <SparklesIcon className="w-5 h-5 text-purple-500" />
              <span className="text-xs font-semibold text-red-600">↓ 3%</span>
            </div>
            <p className="text-xs text-gray-600 mb-1">Cost/kg</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency((summary?.totalExpenses || 0) / (summary?.totalProduction || 1))}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Crop Geography</h2>
                <p className="text-xs text-gray-500">Mapped production areas with Rwanda district context.</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700 border border-emerald-100">
                {cropMapFarmers.length || mapFarmers.length} mapped fields
              </span>
            </div>
          </div>
          <div className="h-[360px] bg-slate-100">
            <MapBaseComponent
              farmers={cropMapFarmers}
              cohorts={[]}
              warehouses={[]}
              trees={[]}
              center={RWANDA_DEFAULTS.center}
              zoom={8}
              baseLayer="terrain"
              adminBoundaryLevel="none"
              showLocationTracker={false}
              sizeToken={`crops-${selectedCropType || 'all'}-${cropMapFarmers.length}`}
            />
          </div>
        </div>

      {/* Crop Portfolio Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CubeIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Crop Portfolio</h2>
            </div>
                <span className="text-xs text-gray-600 font-medium">{cropPortfolio.length} crop types</span>
              </div>
            </div>
            <div className="p-4">
              <DataTable
                columns={columns}
                data={cropPortfolio}
                searchable
                searchPlaceholder="Search crop types..."
                onRowClick={(row) => setSelectedCropType(row.crop_type)}
              />
            </div>
          </div>

      {/* Seasonal Crop View */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-amber-50 px-4 py-3 border-b border-gray-200">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <CalendarDaysIcon className="w-5 h-5 text-emerald-700" />
              <div>
                <h2 className="text-lg font-bold text-gray-900">Seasonal View</h2>
                <p className="text-xs text-gray-500">Click a crop to inspect its harvest windows and cycles.</p>
              </div>
            </div>
            <select
              value={selectedCropType || cropPortfolio[0]?.crop_type || ''}
              onChange={(event) => setSelectedCropType(event.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 outline-none focus:border-emerald-600"
            >
              {cropPortfolio.map((crop) => (
                <option key={crop.crop_type} value={crop.crop_type}>{crop.crop_type}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="p-4">
          {Object.keys(seasonalGroups).length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-8 text-center">
              <Squares2X2Icon className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm font-semibold text-gray-500">No seasonal records yet.</p>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {Object.entries(seasonalGroups).map(([season, records]) => {
                const production = records.reduce((sum, item) => sum + (parseFloat(item.total_production_kg as any) || 0), 0);
                const revenue = records.reduce((sum, item) => sum + (parseFloat(item.gross_revenue as any) || 0), 0);
                const area = records.reduce((sum, item) => sum + (parseFloat(item.planted_area_ha as any) || 0), 0);
                return (
                  <button
                    key={season}
                    onClick={() => fetchCropDetails(records[0].crop_type)}
                    className="rounded-xl border border-gray-100 bg-gradient-to-br from-white to-emerald-50/50 p-5 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-black text-gray-900">{season}</h3>
                        <p className="mt-1 text-xs font-semibold text-gray-500">{records.length} record{records.length === 1 ? '' : 's'}</p>
                      </div>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">Open</span>
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Production</p>
                        <p className="font-black text-gray-900">{formatNumber(production)} kg</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Revenue</p>
                        <p className="font-black text-gray-900">{formatCurrency(revenue)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Area</p>
                        <p className="font-black text-gray-900">{area.toFixed(2)} ha</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Analytics */}
      {analytics && analytics.revenueByType.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <ChartBarIcon className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-bold text-gray-900">Revenue by Crop</h3>
            </div>
            <div className="space-y-3">
              {analytics.revenueByType.map((item, index) => {
                const maxRevenue = Math.max(...analytics.revenueByType.map(i => i.revenue));
                const percentage = (item.revenue / maxRevenue) * 100;
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-gray-700">{item.crop_type}</span>
                      <span className="font-bold text-gray-900">{formatCurrency(item.revenue)}</span>
                    </div>
                    <div className="relative w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <ChartPieIcon className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Production Trends</h3>
            </div>
            <div className="space-y-2">
              {analytics.productionTrends.slice(0, 6).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{item.crop_type}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(item.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">{formatNumber(item.production_kg)}</div>
                    <div className="text-xs text-gray-500">kg</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateCropModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchData}
        existingCropTypes={crops.map((c) => c.crop_type)}
      />
      
      <CropDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        cropData={selectedCrop}
      />
      </div>
    </div>
  );
};

export default CropsPage;
